import React, { useGlobal, useEffect, useState } from "reactn";
import "../../../assets/css/componentSpecificCss/reportAnalysisPages.css";
import { format, parse, fromUnixTime, addDays } from "date-fns";
import _ from "lodash";
import axios from "axios";

import { useDateFilter, CENTS_IN_DOLLAR } from "../../../providers/reports";
import {
  DateFilter,
  ReportContainer,
  ReportTable
} from "../../../components/UI/reportsAnalytics";

import {
  showPlan,
  getPlusMinus,
  getSubtotal,
  formatMoney
} from "../../../utilities/reportsAnalytics";
import { useCancellableRequest } from "../../../utilities/hooks/useCancellableRequest";

const TransactionsReport = () => {
  const orgData = useGlobal("organizationInfo")[0];
  const [loading, setLoading] = useGlobal("loading");
  const [partnerPathway, setPartnerPathway] = useGlobal("partnerPathway");
  const { makeRequest } = useCancellableRequest();

  const [dateFrom, dateTo, setDateFrom, setDateTo] = useDateFilter();
  const [submittedFilters, setSubmittedFilters] = useState(null);

  const [orders, setOrders] = useState([]);
  const [displayOrders, setDisplayOrders] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalInvoiceRevenue, setTotalInvoiceRevenue] = useState(0);

  const [showChildren, setShowChildren] = useState(false);

  const getHeadings = () => {
    if (
      displayOrders.length < 1 ||
      (!showChildren && displayOrders[0].length === 5)
    ) {
      return [
        { label: "Customer" },
        { label: "Date" },
        { label: "Item" },
        { label: "+ / -" },
        {
          label: "Total",
          className: "table-item-money",
          style: { textAlign: "right" }
        }
      ];
    } else {
      return [
        { label: "Customer" },
        { label: "Child" },
        { label: "Child Birthdate" },
        { label: "Date" },
        { label: "Item" },
        { label: "+ / -" },
        {
          label: "Total",
          className: "table-item-money",
          style: { textAlign: "right" }
        }
      ];
    }
  };

  const [totalRefunds, setTotalRefunds] = useState(0);

  useEffect(() => {
    setPartnerPathway([
      ...partnerPathway.slice(0, 1),
      { label: "Reports & Analytics", to: "/reporting" },
      { label: "Transactions Report", to: "/reports/transactions-report" }
    ]);
    fetchInitialData();
  }, []);

  // useEffect(() => {
  //   setDisplayOrders(getDisplayableData(orders));
  //   setTotalRevenue(getTotalRevenue(orders));
  //   setTotalInvoiceRevenue(getTotalInvoiceRevenue(orders));
  // }, [dateFrom, dateTo, showChildren]);

  const applyFilters = () => {
    setSubmittedFilters({
      from: dateFrom,
      to: dateTo,
      showChildren
    });
  };

  useEffect(() => {
    if (!submittedFilters || !orders.length) {
      setDisplayOrders([]);
      setTotalRevenue(0);
      setTotalInvoiceRevenue(0);
      return;
    }

    const { from, to, showChildren } = submittedFilters;

    const getDisplayableDataSubmitted = orders => {
      const dataWithinRange = orders.filter(
        e =>
          new Date(e.date) > new Date(from) &&
          new Date(e.date) < addDays(new Date(to), 1) &&
          e.type !== "Invoice"
      );

      const sortedData = dataWithinRange.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );

      const cleanedData = cleanData(sortedData);

      const formatDateTotal = cleanedData.map(o => ({
        ...o,
        date: format(new Date(o.date), "MMM d, yyyy"),
        total: formatMoney(o.total / 100)
      }));

      return showChildren
        ? formatData(formatDateTotal)
        : formatData(formatDateTotal);
    };

    const getTotalRevenueSubmitted = orders => {
      const dataWithinRange = orders.filter(
        e =>
          new Date(e.date) > new Date(from) &&
          new Date(e.date) < addDays(new Date(to), 1) &&
          e.type !== "Invoice"
      );

      const total = dataWithinRange.reduce((a, c) => a + parseInt(c.total), 0);
      return formatMoney(total / CENTS_IN_DOLLAR);
    };

    const getTotalInvoiceRevenueSubmitted = orders => {
      const invoiceRevenue = orders
        .filter(
          e =>
            e.type === "Invoice" &&
            new Date(e.date) >= new Date(from) &&
            new Date(e.date) <= addDays(new Date(to), 1)
        )
        .reduce((prev, curr) => prev + parseInt(curr.total), 0);

      return formatMoney(invoiceRevenue / CENTS_IN_DOLLAR);
    };

    setDisplayOrders(getDisplayableDataSubmitted(orders));
    setTotalRevenue(getTotalRevenueSubmitted(orders));
    setTotalInvoiceRevenue(getTotalInvoiceRevenueSubmitted(orders));
  }, [orders, submittedFilters]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const result = await makeRequest({
        method: "get",
        url: `${process.env.REACT_APP_API}/partners/order-tracking`
      });

      if (!result) return;

      const allOrders = [
        ...result.data.data.Once.payments,
        ...result.data.data.Recurring.payments
      ];

      setOrders(allOrders);
      setDisplayOrders(getDisplayableData(allOrders));
      setTotalRevenue(getTotalRevenue(allOrders));
      setTotalInvoiceRevenue(getTotalInvoiceRevenue(allOrders));

      setTotalRefunds(
        formatMoney(
          allOrders.reduce(
            (prev, curr) => prev + parseInt(curr.refundedAmount),
            0
          ) / 100
        )
      );
    } catch (error) {
      console.error("Error fetching transactions data:", error);
    } finally {
      setLoading(false);
    }
  };

  const cleanData = orders => {
    return orders.map(o => {
      let obj = {
        customer: o.purchaser,
        childInfo: o.childInfo,
        date: o.date,
        listing: o.item,
        modifiers: getModifierString(o),
        total: o.total
      };
      return obj;
    });
  };

  const getDisplayableData = orders => {
    const dataWithinRange = orders.filter(
      e =>
        new Date(e.date) > new Date(dateFrom) &&
        new Date(e.date) < addDays(new Date(dateTo), 1) &&
        e.type !== "Invoice"
    );
    const sortedData = dataWithinRange.sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
    const cleanedData = cleanData(sortedData);
    const formatDateTotal = cleanedData.map(o => ({
      ...o,
      date: format(new Date(o.date), "MMM d, yyyy"),
      total: formatMoney(o.total / 100)
    }));
    console.log(sortedData);
    return formatData(formatDateTotal);
  };

  const formatData = orders => {
    if (showChildren) {
      return orders.map(o => {
        let childNames = "";
        let childBirthdates = "";

        if (o.childInfo) {
          const children = o.childInfo.map(child => child.split(","));
          childNames = children.map(child => child[0]).join("; ");
          childBirthdates = children
            .map(child => format(new Date(child[1]), "MMM d, yyyy"))
            .join("; ");
        }

        return [
          o.customer,
          childNames,
          childBirthdates,
          o.date,
          o.listing,
          o.modifiers,
          o.total
        ];
      });
    } else {
      return orders.map(o => [
        o.customer,
        o.date,
        o.listing,
        o.modifiers,
        o.total
      ]);
    }
  };

  const getModifierString = order => {
    // modifiers are: tax, credit card fee, promotion, coupon, addOnSemesterItemAmount, gift discount, discount amount, refund
    return Object.keys(order.modifiers)
      .map(modifier =>
        parseInt(order.modifiers[modifier])
          ? `${getModifierName(modifier)} (${formatMoney(
              order.modifiers[modifier] / 100
            )})`
          : ""
      )
      .filter(e => e !== "")
      .join(" / ");
  };

  // tax -> Tax
  // creditCardFee -> Credit Card Fee
  // promotion -> Promotion
  // coupon -> Coupon
  // addOnSemesterItem -> Add On Semester Item
  // giftDiscount -> Gift Discount
  // discount -> Discount
  // refunded -> Refunded
  const getModifierName = modifier => {
    switch (modifier) {
      case "tax":
        return "Tax";
      case "creditCardFee":
        return "Credit Card Fee";
      case "promotion":
        return "Promotion";
      case "coupon":
        return "Coupon";
      case "addOnSemesterItem":
        return "Add On Semester Item";
      case "giftDiscount":
        return "Gift Discount";
      case "discount":
        return "Discount";
      case "refunded":
        return "Refunded";
      default:
        return "";
    }
  };

  const getRevenueChange = orders => {
    let revenueChange = 0;
    orders.forEach(order => {
      revenueChange += parseInt(order.modifiers.tax) || 0;
      revenueChange += parseInt(order.modifiers.creditCardFee) || 0;
      revenueChange -= parseInt(order.modifiers.promotion) || 0;
      revenueChange -= parseInt(order.modifiers.coupon) || 0;
      revenueChange += parseInt(order.modifiers.addOnSemesterItem) || 0;
      revenueChange -= parseInt(order.modifiers.giftDiscount) || 0;
      revenueChange -= parseInt(order.modifiers.discount) || 0;
      revenueChange -= parseInt(order.modifiers.refunded) || 0;
    });

    return revenueChange;
  };

  /**
   * Gets total revenue by aggregating all order data.
   *
   * @returns {Number} Total revenue.
   */
  const getTotalRevenue = orders => {
    const dataWithinRange = orders.filter(
      e =>
        new Date(e.date) > new Date(dateFrom) &&
        new Date(e.date) < addDays(new Date(dateTo), 1) &&
        e.type !== "Invoice"
    );
    const totalRevenue = dataWithinRange.reduce(
      (a, c) => a + parseInt(c.total),
      0
    );

    return formatMoney(totalRevenue / CENTS_IN_DOLLAR);
  };

  const getTotalInvoiceRevenue = orders => {
    console.log("DATES: ", new Date(dateFrom), new Date(dateTo));
    const invoiceRevenue = orders
      .filter(
        e =>
          e.type === "Invoice" &&
          new Date(e.date) >= new Date(dateFrom) &&
          new Date(e.date) <= addDays(new Date(dateTo), 1)
      )
      .reduce((prev, curr) => prev + parseInt(curr.total), 0);

    return formatMoney(invoiceRevenue / CENTS_IN_DOLLAR);
  };

  const csvHeaders = [
    { label: "Customer Name", key: "name" },
    { label: "Children Info", key: "childInfo" },
    { label: "Transaction Datetime", key: "dateTime" },
    { label: "Listing", key: "listingName" },
    { label: "Promo", key: "promo" },
    { label: "Coupon", key: "coupon" },
    { label: "Refunds", key: "refund" },
    { label: "Tax", key: "tax" },
    { label: "Credit Card Fee", key: "creditCardFee" },
    { label: "Add On Semester Item", key: "addOnSemesterItem" },
    { label: "Amount", key: "amount" }
  ];

  /**
   * Gets the CSV for the CSVLink component to use.
   *
   * @returns {Array<Object>} Each property of each object corresponds to a column.
   */
  const getCsvData = () => {
    if (!submittedFilters) return [];

    const { from, to } = submittedFilters;

    return orders
      .filter(
        e =>
          new Date(e.date) > new Date(from) &&
          new Date(e.date) < addDays(new Date(to), 1)
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .map(row => ({
        name: row.purchaser,
        childInfo: row.childInfo,
        dateTime: row.date,
        listingName: row.item,
        promo: row.modifiers.promotion,
        coupon: row.modifiers.coupon,
        refund: row.modifiers.refunded,
        tax: row.modifiers.tax,
        creditCardFee: row.modifiers.creditCardFee,
        addOnSemesterItem: row.modifiers.addOnSemesterItem,
        amount: row.total
      }));
  };

  const reportSubtitles = [
    orgData.organization_title,
    submittedFilters
      ? `For ${format(
          parse(submittedFilters.from, "yyyy-MM-dd", new Date()),
          "MMM d, yyyy"
        )} - ${format(
          parse(submittedFilters.to, "yyyy-MM-dd", new Date()),
          "MMM d, yyyy"
        )}`
      : ""
  ];

  return (
    <div className="admin">
      <div className="container-fluid adminprofiles">
        <div className="row cont">
          <div className="col-md-6">
            <h1>Transaction Report</h1>
          </div>
        </div>

        <div className="filter-container">
          <div className="filter-section">
            <DateFilter
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
            />
            <div className="filter-item">
              <label className="filter-label" htmlFor="show_children">
                Show Children
              </label>
              <input
                id="show_children"
                type="checkbox"
                value={showChildren}
                onChange={e => {
                  setShowChildren(e.target.checked);
                }}
              />
            </div>

            <button className="btn btn-primary ml-3" onClick={applyFilters}>
              Apply
            </button>
          </div>
        </div>
        <ReportContainer
          title="Transaction Report"
          subtitles={reportSubtitles}
          csvHeaders={csvHeaders}
          csvData={getCsvData()}
          fileName={
            submittedFilters
              ? `Transaction_${submittedFilters.from}-${submittedFilters.to}.csv`
              : "Transactions.csv"
          }
          isLarge
        >
          <div className="report-table-large">
            <ReportTable
              headings={[
                { label: "Summary", className: "table-item-bold" },
                { label: "", className: "table-item-money" }
              ]}
              body={[
                ["Total Listings Revenue for Time Period", totalRevenue],
                ["Total Invoice Revenue for Time Period", totalInvoiceRevenue],
                ["Total Refunds", totalRefunds]
              ]}
            />
            {!submittedFilters && (
              <p className="text-center mt-4">
                Please select filters and press Apply to view results.
              </p>
            )}
            <ReportTable headings={getHeadings()} body={displayOrders} />
          </div>
        </ReportContainer>
      </div>
    </div>
  );
};

export default TransactionsReport;
